/**
 * Derives an ISO 4217 currency code from a BCP 47 locale (e.g. `navigator.language`).
 *
 * The account-less receipt search sends an optional amount filter to buyerportal, which needs an
 * explicit currency to interpret the amount's minor units. The buyer never picks a currency, so we
 * infer one from their locale — matching the legacy behavior where the receipts service inferred the
 * currency server-side from the request locale.
 */

const DEFAULT_CURRENCY = 'USD';

// Square receipt-lookup markets, keyed by the locale's region (country) subtag.
const REGION_TO_CURRENCY: Record<string, string> = {
  AU: 'AUD',
  CA: 'CAD',
  ES: 'EUR',
  FR: 'EUR',
  GB: 'GBP',
  IE: 'EUR',
  JP: 'JPY',
  US: 'USD',
};

// Fallback when the locale has no region subtag (e.g. plain "en", "ja").
const LANGUAGE_TO_CURRENCY: Record<string, string> = {
  en: 'USD',
  es: 'EUR',
  fr: 'EUR',
  ja: 'JPY',
};

export function getCurrencyFromLocale(
  locale: string = typeof navigator === 'undefined' ? '' : navigator.language
): string {
  if (!locale) {
    return DEFAULT_CURRENCY;
  }

  const [language, region] = locale.split('-');

  if (region && REGION_TO_CURRENCY[region.toUpperCase()]) {
    return REGION_TO_CURRENCY[region.toUpperCase()];
  }

  if (language && LANGUAGE_TO_CURRENCY[language.toLowerCase()]) {
    return LANGUAGE_TO_CURRENCY[language.toLowerCase()];
  }

  return DEFAULT_CURRENCY;
}
